import { useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';

interface BrandMultiSelectProps {
  brands: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  disabled?: boolean;
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm disabled:bg-gray-50 disabled:text-gray-500';

/**
 * Multi-select per le marche target di una promozione.
 * Mantiene l'ordine di selezione e mostra le marche scelte come chip rimovibili.
 */
export default function BrandMultiSelect({
  brands,
  selected,
  onChange,
  disabled = false,
}: BrandMultiSelectProps) {
  const [search, setSearch] = useState('');

  const filteredBrands = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return brands;
    return brands.filter((b) => b.toLowerCase().includes(q));
  }, [brands, search]);

  const toggle = (brand: string) => {
    if (selected.includes(brand)) {
      onChange(selected.filter((b) => b !== brand));
    } else {
      onChange([...selected, brand]);
    }
  };

  const remove = (brand: string) => {
    onChange(selected.filter((b) => b !== brand));
  };

  return (
    <div className="space-y-2">
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((brand) => (
            <span
              key={brand}
              className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20"
            >
              {brand}
              {!disabled && (
                <button
                  type="button"
                  onClick={() => remove(brand)}
                  aria-label={`Rimuovi ${brand}`}
                  className="rounded-full p-0.5 hover:bg-blue-100"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      {!disabled && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca marca…"
            className={`${inputClass} pl-9`}
          />
        </div>
      )}

      <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
        {filteredBrands.length === 0 ? (
          <p className="px-3 py-2.5 text-sm text-gray-400 italic">Nessuna marca trovata</p>
        ) : (
          filteredBrands.map((brand) => (
            <label
              key={brand}
              className={`flex items-center gap-2.5 px-3 py-2 text-sm ${
                disabled ? 'cursor-default' : 'cursor-pointer hover:bg-gray-50'
              }`}
            >
              <input
                type="checkbox"
                checked={selected.includes(brand)}
                onChange={() => toggle(brand)}
                disabled={disabled}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-gray-900">{brand}</span>
            </label>
          ))
        )}
      </div>
    </div>
  );
}
